/**
 * Emissions Bulk Import Page
 *
 * Import activity data in bulk from CSV or Excel files.
 * Scope 1: Select Scope → Select Activity Type → Upload → Preview → Import → Results
 * Scope 2: Select Scope → Upload → Preview → Import → Results
 */

import { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  ArrowLeft, Download, Upload, CheckCircle, AlertTriangle, List, Loader2,
  Flame, Zap, Info, Wind, Cog, Car,
} from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { ActivitySelectionCard } from '../components/ActivitySelectionCard';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { ImportFileDropzone, MINE_TYPES } from '@features/files/ImportFileDropZone';
import { generateUrl } from '@routes/util';
import { emissionsTrackerRoutes } from '../routes';
import { downloadBlob } from '@utils/downloadBlob';
import { useCreateBulkImportMutation, useLazyGetActivityImportTemplateQuery } from '../api/emissions';
import { EmissionScope, Scope1ActivityType, CreateActivityRequest, type BulkImport } from '../types/emissions';
import { getScopeLabel, getActivityTypeLabel } from '../utils/bulkImportTemplates';
import { parseImportFile, ParseResult, toActivityRequests } from '../utils/bulkImportParser';
import { getErrorMessage } from '@utils/error';
import { BulkImportPreview, ErrorSummary } from '../components/BulkImportPreview';

type ImportStep = 'select' | 'activity-type' | 'upload' | 'preview' | 'importing' | 'complete';

type ImportResult = BulkImport;

import { type ElementType } from 'react';

interface Scope1ActivityTypeConfig {
  type: Scope1ActivityType;
  title: string;
  description: string;
  Icon: ElementType;
  iconBg: string;
  iconColor: string;
}

const SCOPE1_ACTIVITY_TYPE_CONFIGS: Scope1ActivityTypeConfig[] = [
  {
    type: Scope1ActivityType.StationaryCombustion,
    title: 'Stationary Combustion',
    description: 'Boilers, furnaces, generators, and other fixed combustion equipment burning fuel on-site',
    Icon: Flame,
    iconBg: 'tw:bg-orange-100',
    iconColor: 'tw:text-orange-600',
  },
  {
    type: Scope1ActivityType.MobileCombustion,
    title: 'Mobile Combustion',
    description: 'Company-owned vehicles: cars, vans, trucks, and other mobile sources',
    Icon: Car,
    iconBg: 'tw:bg-blue-100',
    iconColor: 'tw:text-blue-600',
  },
  {
    type: Scope1ActivityType.FugitiveEmissions,
    title: 'Fugitive Emissions',
    description: 'Refrigerant leaks from AC and cooling systems, SF6 from electrical equipment',
    Icon: Wind,
    iconBg: 'tw:bg-teal-100',
    iconColor: 'tw:text-teal-600',
  },
  {
    type: Scope1ActivityType.ProcessEmissions,
    title: 'Process Emissions',
    description: 'Industrial process emissions from cement, steel, aluminium, or chemical production',
    Icon: Cog,
    iconBg: 'tw:bg-slate-100',
    iconColor: 'tw:text-slate-600',
  },
];

export const EmissionsBulkImportPage = () => {
  const history = useHistory();
  const { initiativeId = '' } = useParams<{ initiativeId: string }>();

  // Redirect to the unified Add Activity wizard which now includes Bulk Upload
  useEffect(() => {
    history.replace(generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_NEW, { initiativeId }));
  }, [history, initiativeId]);

  // State
  const [step, setStep] = useState<ImportStep>('select');
  const [selectedScope, setSelectedScope] = useState<EmissionScope | null>(null);
  const [selectedActivityType, setSelectedActivityType] = useState<Scope1ActivityType | null>(null);
  const [importFile, setImportFile] = useState<File | undefined>();
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // API
  const [createImport, { isLoading: isImporting }] = useCreateBulkImportMutation();
  const [fetchTemplate, { isFetching: isDownloadingTemplate }] = useLazyGetActivityImportTemplateQuery();

  const handleBack = () => {
    if (step === 'select') {
      history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ADMIN, { initiativeId }));
    } else if (step === 'activity-type') {
      setStep('select');
    } else if (step === 'upload') {
      resetFileState();
      setStep(selectedScope === EmissionScope.Scope1 ? 'activity-type' : 'select');
    } else if (step === 'preview') {
      resetFileState();
      setStep('upload');
    } else if (step === 'complete') {
      history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITIES, { initiativeId }));
    }
  };

  const resetFileState = () => {
    setImportFile(undefined);
    setParseResult(null);
    setParseError(null);
    setImportError(null);
    setImportResult(null);
  };

  const handleScopeSelect = (scope: EmissionScope) => {
    setSelectedScope(scope);
    setSelectedActivityType(null);
    resetFileState();
    setStep(scope === EmissionScope.Scope1 ? 'activity-type' : 'upload');
  };

  const handleActivityTypeSelect = (activityType: Scope1ActivityType) => {
    setSelectedActivityType(activityType);
    resetFileState();
    setStep('upload');
  };

  const handleDownloadTemplate = async (scope: EmissionScope) => {
    const result = await fetchTemplate({
      initiativeId,
      scope,
      ...(selectedActivityType && { activityType: selectedActivityType }),
    });
    if (result.data) {
      const activitySuffix = selectedActivityType ? `-${selectedActivityType}` : '';
      downloadBlob(result.data, `emissions-${scope}${activitySuffix}-import-template.xlsx`);
    } else if (result.error) {
      setParseError('Template download failed. Please try again.');
    }
  };

  const handleFilesAdded = async (files: File[], scope: EmissionScope) => {
    const file = files[0];
    if (!file) {
      return;
    }

    setImportFile(file);
    setParseError(null);

    try {
      const result = await parseImportFile(file, scope);
      setParseResult(result);
      setStep('preview');
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Failed to parse file');
      setParseResult(null);
    }
  };

  const handleFileRemove = () => {
    setImportFile(undefined);
    setParseResult(null);
    setParseError(null);
    setStep('upload');
  };

  const handleImport = async (result: ParseResult) => {
    if (!selectedScope || !importFile) {
      return;
    }

    const activities: CreateActivityRequest[] = toActivityRequests(result);

    if (activities.length === 0) {
      return;
    }

    setStep('importing');

    try {
      const result = await createImport({
        initiativeId,
        file: importFile,
        activities: JSON.stringify(activities),
        scope: selectedScope,
        regionFallback: true,
        yearFallback: true,
      }).unwrap();
      setImportResult(result);
      setStep('complete');
    } catch (error) {
      setImportError(getErrorMessage(error));
      setStep('preview');
    }
  };

  const handleCancel = () => {
    resetFileState();
    setStep('upload');
  };

  const handleViewActivities = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITIES, { initiativeId }));
  };

  const handleImportMore = () => {
    setSelectedScope(null);
    setSelectedActivityType(null);
    resetFileState();
    setStep('select');
  };

  const getUploadSubtitle = () => {
    if (!selectedScope) {
      return '';
    }
    if (selectedScope === EmissionScope.Scope2) {
      return `Upload ${getScopeLabel(selectedScope)} activities`;
    }
    if (selectedActivityType) {
      return `Upload Scope 1 – ${getActivityTypeLabel(selectedActivityType)} activities`;
    }
    return `Upload ${getScopeLabel(selectedScope)} activities`;
  };

  return (
    <Dashboard>
      {/* Page Header */}
      <div className='tw:flex tw:items-center tw:gap-3 tw:mb-6'>
        <Button variant='ghost' size='icon' onClick={handleBack} className='tw:text-muted-foreground' aria-label='Go back'>
          <ArrowLeft className='tw:size-4' />
        </Button>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Bulk Import</h4>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
            {step === 'select' && 'Select emission scope to import'}
            {step === 'activity-type' && 'Select activity type'}
            {step === 'upload' && getUploadSubtitle()}
            {step === 'preview' && 'Review and confirm import'}
            {step === 'importing' && 'Importing activities...'}
            {step === 'complete' && 'Import complete'}
          </p>
        </div>
      </div>

      {/* Step 1: Select Scope */}
      {step === 'select' && (
        <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6'>
          <ActivitySelectionCard
            Icon={Flame}
            iconBg='tw:bg-orange-100'
            iconColor='tw:text-orange-600'
            label='Scope 1 – Direct Emissions'
            description='Stationary combustion, mobile combustion, process emissions, fugitive emissions'
            actionLabel='Select & Import'
            onClick={() => handleScopeSelect(EmissionScope.Scope1)}
          />
          <ActivitySelectionCard
            Icon={Zap}
            iconBg='tw:bg-yellow-100'
            iconColor='tw:text-yellow-600'
            label='Scope 2 – Purchased Energy'
            description='Purchased electricity, steam, heating, and cooling'
            actionLabel='Select & Import'
            onClick={() => handleScopeSelect(EmissionScope.Scope2)}
          />
        </div>
      )}

      {/* Step 1b: Select Activity Type (Scope 1 only) */}
      {step === 'activity-type' && (
        <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6'>
          {SCOPE1_ACTIVITY_TYPE_CONFIGS.map((config) => (
            <ActivitySelectionCard
              key={config.type}
              Icon={config.Icon}
              iconBg={config.iconBg}
              iconColor={config.iconColor}
              label={config.title}
              description={config.description}
              actionLabel='Select & Download Template'
              onClick={() => handleActivityTypeSelect(config.type)}
            />
          ))}
        </div>
      )}

      {/* Step 2: Upload File */}
      {step === 'upload' && selectedScope && (
        <>
          {/* Template Download */}
          <Card className='tw:mb-4'>
            <CardContent className='tw:p-4'>
              <div className='tw:flex tw:justify-between tw:items-center'>
                <div>
                  <h5 className='tw:font-semibold tw:mb-1'>Step 1: Download Template</h5>
                  <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
                    Download the XLSX template and fill it in with your data
                  </p>
                </div>
                <Button variant='outline' onClick={() => handleDownloadTemplate(selectedScope)} disabled={isDownloadingTemplate}>
                  <Download className='tw:size-4' />
                  {isDownloadingTemplate ? 'Downloading...' : 'Download Template'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Upload Section */}
          <Card className='tw:mb-4'>
            <CardContent className='tw:p-4'>
              <div className='tw:mb-4'>
                <h5 className='tw:font-semibold tw:mb-1'>Step 2: Upload Your File</h5>
                <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
                  Fill in the template with your data and upload it here
                </p>
              </div>

              <ImportFileDropzone
                importFile={importFile}
                onFilesAdded={(files) => handleFilesAdded(files, selectedScope)}
                onFileRemove={handleFileRemove}
                accept={{
                  ...MINE_TYPES.CSV,
                  ...MINE_TYPES.EXCEL,
                }}
                dropText='Drag and drop your CSV or Excel file here'
                hint='Supported formats: CSV, XLSX, XLS'
              />

              {parseError && (
                <div className='tw:flex tw:gap-2 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-red-800 tw:text-sm tw:mt-3'>
                  <AlertTriangle className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
                  {parseError}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Import Guidelines */}
          <ImportGuidelines scope={selectedScope} activityType={selectedActivityType ?? undefined} />
        </>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && parseResult && (
        <>
          {importError && (
            <div className='tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:mb-4 tw:flex tw:items-center tw:gap-2 tw:text-red-800 tw:text-sm'>
              <AlertTriangle className='tw:size-4 tw:shrink-0' />
              {importError}
            </div>
          )}
          <BulkImportPreview
            parseResult={parseResult}
            onConfirm={() => handleImport(parseResult)}
            onCancel={handleCancel}
            isImporting={isImporting}
          />
          <ErrorSummary parseResult={parseResult} />
        </>
      )}

      {/* Step 4: Importing */}
      {step === 'importing' && (
        <Card>
          <CardContent className='tw:p-10 tw:text-center'>
            <Loader2 className='tw:size-10 tw:animate-spin tw:text-muted-foreground tw:mx-auto tw:mb-4' />
            <p className='tw:font-medium tw:mb-1'>
              Importing {parseResult?.validCount ?? 0} activities...
            </p>
            <p className='tw:text-sm tw:text-muted-foreground'>
              Emissions will be automatically calculated using the service
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Complete */}
      {step === 'complete' && importResult && (
        <Card>
          <CardContent className='tw:p-6'>
            <div className='tw:text-center tw:mb-6'>
              <div
                className={`tw:rounded-full tw:inline-flex tw:items-center tw:justify-center tw:mb-4 ${
                  importResult.failedRows === 0 ? 'tw:bg-emerald-100' : 'tw:bg-amber-100'
                }`}
                style={{ width: 64, height: 64 }}
              >
                {importResult.failedRows === 0 ? (
                  <CheckCircle className='tw:size-8 tw:text-emerald-600' />
                ) : (
                  <AlertTriangle className='tw:size-8 tw:text-amber-600' />
                )}
              </div>
              <h5 className='tw:font-semibold tw:mb-2'>Import Complete</h5>
              <p className='tw:text-sm tw:text-muted-foreground'>
                <strong>{importResult.successfulRows}</strong> activit
                {importResult.successfulRows === 1 ? 'y' : 'ies'} created successfully
                {importResult.failedRows > 0 && (
                  <>
                    , <strong className='tw:text-destructive'>{importResult.failedRows}</strong> failed
                  </>
                )}
              </p>
            </div>

            {/* Failed items */}
            {importResult.failedRows > 0 && (
              <div className='tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-4 tw:mb-6'>
                <h5 className='tw:font-semibold tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:text-amber-800 tw:text-sm'>
                  <AlertTriangle className='tw:size-4' />
                  Some activities failed to import
                </h5>
                <ul className='tw:text-sm tw:text-amber-700 tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
                  {importResult.rowResults
                    .filter((r) => !r.success)
                    .slice(0, 5)
                    .map((r) => (
                      <li key={r.index}>
                        Row {r.index + 2}: {r.error || 'Unknown error'}
                      </li>
                    ))}
                  {importResult.rowResults.filter((r) => !r.success).length > 5 && (
                    <li className='tw:text-amber-600'>
                      ...and {importResult.rowResults.filter((r) => !r.success).length - 5} more
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Calculation warnings */}
            {importResult.rowResults.some((r) => r.warning) && (() => {
              const warningCount = importResult.rowResults.filter((r) => r.warning).length;
              return (
                <div className='tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-4 tw:mb-6'>
                  <h5 className='tw:font-semibold tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:text-amber-800 tw:text-sm'>
                    <AlertTriangle className='tw:size-4' />
                    {warningCount} {warningCount === 1 ? 'activity' : 'activities'} saved without emission calculations
                  </h5>
                  <p className='tw:text-sm tw:text-amber-700 tw:mb-0'>
                    These activities were created but their emissions could not be calculated.
                    Review and correct the activity details (e.g. region, fuel type, or unit) to resolve.
                  </p>
                </div>
              );
            })()}

            {/* Actions */}
            <div className='tw:flex tw:justify-center tw:gap-3'>
              <Button variant='outline' onClick={handleImportMore}>
                <Upload className='tw:size-4' />
                Import More
              </Button>
              <Button onClick={handleViewActivities}>
                <List className='tw:size-4' />
                View Activities
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </Dashboard>
  );
};

/**
 * Import guidelines component — also used by the unified Add Activity wizard
 */
export interface ImportGuidelinesProps {
  scope: EmissionScope;
  activityType?: Scope1ActivityType;
}

export const ImportGuidelines = ({ activityType }: ImportGuidelinesProps) => {
  return (
    <Card className='tw:mb-4'>
      <CardContent className='tw:p-4'>
        <div className='tw:flex tw:gap-3'>
          <Info className='tw:size-5 tw:text-blue-600 tw:shrink-0 tw:mt-2' />
          <div>
            <h5 className='tw:font-semibold tw:mb-3'>Import Guidelines</h5>
            <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4'>
              <div>
                <h6 className='tw:font-semibold tw:text-sm tw:mb-2'>Required Fields</h6>
                <ul className='tw:text-sm tw:text-muted-foreground tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
                  <li>Activity Type</li>
                  <li>Period Start (YYYY-MM-DD)</li>
                  <li>Period End (YYYY-MM-DD)</li>
                  <li>Quantity (positive number)</li>
                  <li>Unit (select from dropdown)</li>
                  <li>Region (country code)</li>
                  {activityType === Scope1ActivityType.StationaryCombustion && <li>Fuel Type</li>}
                  {activityType === Scope1ActivityType.MobileCombustion && <li>Vehicle Type</li>}
                  {activityType === Scope1ActivityType.FugitiveEmissions && <li>Gas / Refrigerant Type</li>}
                  {activityType === Scope1ActivityType.ProcessEmissions && <li>Process Type</li>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
